import type { Maybe } from '@adornis/base/utilTypes.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { and } from '@adornis/validation/functions/and.js';
import { email } from '@adornis/validation/functions/email.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { formatBillingPositionPrice } from '../../../billingPositions/client/BillingPositionPriceInput.js';
import { translateBillingRhythm } from '../../../billingPositions/entities/BillingPosition.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { Group } from '../../../groups/entities/Group.js';
import { School } from '../../../groups/entities/School.js';
import { useSelectableSchoolIDs } from '../../../permissions/client/useSelectableSchoolIDs.js';
import { Permission } from '../../../permissions/roles.js';
import { Person } from '../../entities/person.js';
import { UserListAPI } from '../../list/api/UserListAPI.js';
import { InvitationOptionsForType, type InviteType } from '../../spender-sorgeberechtigte/invitationOptions.js';
import { SpenderSorgeberechtigteAPI } from '../../spender-sorgeberechtigte/SpenderSorgeberechtigteAPI.js';
import type { FamilyRelative } from '../FamilyRelative.js';

/** an existing link, as the graph hands it to the dialog */
export interface FamilyLinkEdit {
  parentID: string;
  childID: string;
  inviteType: InviteType;
}

type DialogPerson = Pick<FamilyRelative, '_id' | 'vorname' | 'nachname' | 'debitorennummer'>;

type NewPerson = Pick<DialogPerson, 'vorname' | 'nachname'> & { email: string; schoolID: Maybe<string> };

/** stands in for the typed-in person on its side until saving creates it */
const NEW_PERSON_ID = 'new-person';

const PICKER_SELECTION = {
  _id: 1,
  debitorennummer: 1,
  email: 1,
  profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
} as const;

const nameOf = (person: Maybe<Pick<DialogPerson, 'vorname' | 'nachname'>>) =>
  person ? `${person.vorname ?? ''} ${person.nachname ?? ''}`.trim() : '';

/**
 * Adds or edits one link between two persons. Left is the Sorgeberechtigte or Spender, right the child - swapping the
 * sides swaps who holds whom. One side is the person the dialog was opened for, the other one is picked.
 */
export const RelationshipDialog = stateful(
  (props: {
    viewed: DialogPerson;
    /** a new link for a student: the student is the child, the Sorgeberechtigte or Spender is picked */
    viewedAsChild?: boolean;
    /** the persons of the graph, to show both ends of an existing link */
    relatives: DialogPerson[];
    link?: FamilyLinkEdit;
    resolve: (changed: boolean) => void;
    reject: () => void;
  }) => {
    const user = useObservable(() => currentUser<Person>(), []);
    const byID = (id: string) => props.relatives.find(relative => relative._id === id);

    const linkTouchesViewed = !props.link || [props.link.parentID, props.link.childID].includes(props.viewed._id);
    const [pickedLeft, setLeft] = useState<Maybe<DialogPerson>>(
      props.link ? byID(props.link.parentID) : props.viewedAsChild ? null : props.viewed,
    );
    const [pickedRight, setRight] = useState<Maybe<DialogPerson>>(
      props.link ? byID(props.link.childID) : props.viewedAsChild ? props.viewed : null,
    );
    // the side the dialog was opened for stays put, the other one is picked
    const [fixedSide, setFixedSide] = useState<'left' | 'right'>(
      (props.link ? linkTouchesViewed && props.link.childID === props.viewed._id : props.viewedAsChild)
        ? 'right'
        : 'left',
    );
    const pickedSide = fixedSide === 'left' ? 'right' : 'left';
    const [inviteType, setInviteType] = useState<InviteType>(props.link?.inviteType ?? 'Sorgeberechtigter');
    // a filter rebuilt per render carries a fresh _id and refetches the picker
    const [filter] = useState(new AdornisFilter({}));

    /**
     * A person the picker does not find is typed in instead and only created together with the link: school staff
     * find a person only through a role at their school, and the link is what gives a new parent one. A new child
     * starts as SuS-Bewerber at the picked school.
     */
    const [creating, setCreating] = useState(false);
    const newChild = pickedSide === 'right';
    const [newPerson] = useState<NewPerson>({ email: '', vorname: null, nachname: null, schoolID: null });
    const form = useForm<NewPerson>(
      newPerson,
      { validator: { email: and(sgNonOptional(), email()), ...(newChild ? { schoolID: sgNonOptional() } : {}) } },
      [newChild],
    );
    const typedIn: DialogPerson = {
      _id: NEW_PERSON_ID,
      vorname: form.document.vorname,
      nachname: form.document.nachname,
      debitorennummer: null,
    };
    const left = creating && pickedSide === 'left' ? typedIn : pickedLeft;
    const right = creating && pickedSide === 'right' ? typedIn : pickedRight;
    const label = (person: Maybe<DialogPerson>) =>
      person?._id === NEW_PERSON_ID ? nameOf(person) || form.document.email || 'Neue Person' : nameOf(person);

    // as in the user list: schulleitung and sekretariat only get their own schools, a single one is preset
    const selectableSchoolIDs = useSelectableSchoolIDs();
    const [schoolFilter] = useState(
      new AdornisFilter({
        searchFields: ['name'],
        aspects: [
          new BooleanAspect({ customID: 'onlyInternal', value: true }),
          new StringAspect({ fieldName: '_class', value: School._class }),
          ...(selectableSchoolIDs ? [new ListAspect({ fieldName: '_id', values: selectableSchoolIDs })] : []),
        ],
      }),
      [JSON.stringify(selectableSchoolIDs)],
    );
    const onlySchoolID = selectableSchoolIDs?.length === 1 ? selectableSchoolIDs[0] : null;
    useEffect(() => {
      if (onlySchoolID && !form.document.schoolID) form.document.schoolID = onlySchoolID;
    }, [onlySchoolID]);

    const can = (permission: Permission) => user?.hasPermission(permission, ANY_CONTEXT) ?? false;
    const canLink = (type: InviteType) => can(InvitationOptionsForType[type].manualConfirmPermission);
    const canRemove = (type: InviteType) => can(InvitationOptionsForType[type].removePermission);
    const canCreate = !props.link && canLink(inviteType) && (!newChild || can(Permission.CREATE_USER));

    const samePair = props.link && left?._id === props.link.parentID && right?._id === props.link.childID;
    const unchanged = samePair && inviteType === props.link?.inviteType;
    const allowed = !props.link
      ? creating
        ? canCreate
        : canLink(inviteType)
      : samePair
        ? can(Permission.TOGGLE_SPENDER_INVITE_TYPE)
        : canRemove(props.link.inviteType) && canLink(inviteType);
    const complete = !!left && !!right && left._id !== right._id;

    const swap = () => {
      setLeft(pickedRight);
      setRight(pickedLeft);
      setFixedSide(fixedSide === 'left' ? 'right' : 'left');
    };

    const done = (message: string) => {
      void SgSnackbar.show(message);
      props.resolve(true);
    };

    const handleSave = async () => {
      if (!left || !right || unchanged) return;
      if (creating && (await form.validate())) return;
      const link = props.link;
      await guardDialog(
        async () => {
          if (creating) {
            return SpenderSorgeberechtigteAPI.createAndLink(
              inviteType,
              (newChild ? left : right)._id,
              newChild,
              form.document.email.trim(),
              form.document.vorname ?? '',
              form.document.nachname ?? '',
              newChild ? (form.document.schoolID ?? '') : '',
            )();
          }
          if (!link) return SpenderSorgeberechtigteAPI.link(inviteType, right._id, left._id)();
          if (samePair) return SpenderSorgeberechtigteAPI.toggleType(inviteType, right._id, left._id)();
          // another person or the other direction: the old link goes, a new one comes
          await SpenderSorgeberechtigteAPI.remove(link.inviteType, link.childID, link.parentID)();
          return SpenderSorgeberechtigteAPI.link(inviteType, right._id, left._id)();
        },
        { success: () => done(creating ? 'Person angelegt und verknüpft' : 'Beziehung gespeichert') },
      );
    };

    const handleRemove = async () => {
      const link = props.link;
      if (!link) return;
      const ending = await SpenderSorgeberechtigteAPI.mandatesEndedByRemoval(
        link.inviteType,
        link.childID,
        link.parentID,
      )({ _id: 1, name: 1, mandateReference: 1, amountInCents: 1, rhythm: 1 });
      const confirmed = await confirmDialog(
        html`<div class="flex flex-col gap-3">
          <p>Die Beziehung wird entfernt, beide Personen bleiben erhalten.</p>
          ${
            ending.length
              ? html`<p>
                    Diese SEPA-Mandate, die <b>${nameOf(byID(link.parentID))}</b> für die Buchungen von
                    <b>${nameOf(byID(link.childID))}</b> trägt, werden beendet:
                  </p>
                  <ul class="list rounded-box bg-base-200">
                    ${ending.map(
                      mandate => html`
                        <li class="list-row">
                          <div class="flex flex-col gap-0.5">
                            <span class="font-medium">
                              ${mandate.name ?? 'Ohne Mandatsname'} ·
                              ${formatBillingPositionPrice(mandate.amountInCents)}
                              ${mandate.rhythm ? translateBillingRhythm(mandate.rhythm) : nothing}
                            </span>
                            <span class="text-sm text-base-content/70">
                              ${mandate.mandateReference ?? 'Ohne Mandatsreferenz'}
                            </span>
                          </div>
                        </li>
                      `,
                    )}
                  </ul>`
              : html`<p class="text-base-content/70">Davon sind keine SEPA-Mandate betroffen.</p>`
          }
        </div>`,
        { highlightDecline: true, acceptText: 'Entfernen', declineText: 'Abbrechen' },
      ).catch(() => false);
      if (!confirmed) return;
      await guardDialog(() => SpenderSorgeberechtigteAPI.remove(link.inviteType, link.childID, link.parentID)(), {
        success: () => done('Beziehung entfernt'),
      });
    };

    const isSB = inviteType === 'Sorgeberechtigter';
    const personCard = (person: Maybe<DialogPerson>, role: string, fixed: boolean) =>
      person
        ? html`
            <div
              class="card card-border w-full max-w-72 gap-1 bg-base-100 p-4 ${fixed ? 'border-2 border-primary' : ''}"
            >
              <span class="flex min-w-0 items-center gap-2">
                <span class="icon-[material-symbols--person-outline] size-6 shrink-0 text-base-content/70"></span>
                <span class="truncate font-semibold md:text-lg" title=${label(person)}>${label(person)}</span>
              </span>
              <span class="truncate text-sm text-base-content/70">${role}</span>
              ${
                person._id === NEW_PERSON_ID
                  ? html`<span class="flex items-center gap-2">
                      <span class="badge badge-soft badge-primary">Neu</span>
                      <span class="truncate text-sm">${form.document.email}</span>
                    </span>`
                  : html`<span class="truncate tabular-nums">
                      ${person.debitorennummer ? `Debitor ${person.debitorennummer}` : nothing}
                    </span>`
              }
            </div>
          `
        : html`
            <div
              class="card card-border min-h-28 w-full max-w-72 items-center justify-center border-dashed p-4 text-base-content/70"
            >
              Person wählen
            </div>
          `;

    const pick = (person: Maybe<DialogPerson>) => (pickedSide === 'left' ? setLeft(person) : setRight(person));

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: props.link
            ? 'Beziehung bearbeiten'
            : props.viewedAsChild
              ? 'Sorgeberechtigten/Spender hinzufügen'
              : 'Kind hinzufügen',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <!-- in the words of the student tab's Sorgeberechtigte/Spender sections, which this replaces -->
        <p class="text-sm text-base-content/70">
          Ersetzt die Bereiche „Sorgeberechtigte“ und „Spender“ im Schüler-Tab: <b>Hinzufügen</b> entspricht „Einladung
          manuell bestätigen“ für eine vorhandene Person, der Schalter „Zu Spender wechseln“ bzw. „Zu Sorgeberechtigter
          wechseln“, <b>Beziehung entfernen</b> „Verknüpfung löschen“. Wer noch kein Konto hat, wird über
          <b>Neue Person anlegen</b> mit seiner E-Mail-Adresse angelegt und gleich verknüpft.
        </p>

        <div class="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center sm:gap-8">
          ${personCard(left, isSB ? 'Sorgeberechtigt' : 'Spender', fixedSide === 'left')}
          <div class="flex flex-col items-center gap-3 sm:min-w-72">
            <!-- a switch between the two kinds; the active side is highlighted -->
            <div class="flex items-center gap-3 text-sm">
              <span class=${isSB ? 'font-semibold text-primary' : 'text-base-content/70'}>Sorgeberechtigung</span>
              <span class=${isSB ? '' : 'font-semibold text-primary'}>
                ${ToggleInput({
                  value: !isSB,
                  label: 'Spender',
                  onChanged: spender => setInviteType(spender ? 'Spender' : 'Sorgeberechtigter'),
                })}
              </span>
            </div>
            <!-- drawn like the graph: solid for Sorgeberechtigte, dashed for Spender -->
            <div
              class="h-10 w-0 border-l-[3px] border-primary sm:h-0 sm:w-full sm:border-t-[3px] sm:border-l-0 ${isSB ? '' : 'border-dashed'}"
              aria-hidden="true"
            ></div>
            <button
              type="button"
              class="btn btn-circle btn-ghost"
              aria-label="Seiten tauschen"
              title="Seiten tauschen"
              @click=${swap}
            >
              <span class="icon-[material-symbols--swap-horiz-rounded] size-6"></span>
            </button>
          </div>
          ${personCard(right, isSB ? 'Kind' : 'Spendenkind', fixedSide === 'right')}
        </div>

        ${
          creating
            ? html`
                <div class="card card-border flex flex-col gap-3 bg-base-200 p-4">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="text-sm font-semibold text-base-content/70">
                      Neue Person – ${newChild ? 'Kind' : isSB ? 'Sorgeberechtigte Person' : 'Spender'}
                    </span>
                    <button type="button" class="btn btn-ghost btn-sm" @click=${() => setCreating(false)}>
                      <span class="icon-[material-symbols--search-rounded] size-5"></span>
                      Vorhandene Person suchen
                    </button>
                  </div>
                  ${TextInput({ ...form.field('email'), placeholder: 'E-Mail-Adresse' })}
                  <div class="flex flex-col gap-2 sm:flex-row">
                    ${TextInput({ ...form.field('vorname'), placeholder: 'Vorname (optional)' })}
                    ${TextInput({ ...form.field('nachname'), placeholder: 'Nachname (optional)' })}
                  </div>
                  ${
                    newChild
                      ? EntityPickerInput({
                          ...form.field('schoolID'),
                          class: Group,
                          operation: GroupsAPI.subscribeGroups,
                          filter: schoolFilter,
                          placeholder: 'Schule',
                          selectionSet: { _id: 1, name: 1 },
                        })
                      : nothing
                  }
                  <p class="text-sm text-base-content/70">
                    ${
                      newChild
                        ? 'Das Kind wird beim Hinzufügen als SuS-Bewerber an der gewählten Schule angelegt'
                        : 'Die Person wird beim Hinzufügen angelegt'
                    }
                    und erhält eine E-Mail mit einem Link, um ihr Passwort zu vergeben.
                  </p>
                </div>
              `
            : html`
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-semibold text-base-content/70">
                    ${pickedSide === 'left' ? (isSB ? 'Sorgeberechtigte Person' : 'Spender') : 'Kind'}
                  </span>
                  ${EntityPickerInput({
                    value: (pickedSide === 'left' ? left : right)?._id ?? null,
                    class: Person,
                    operation: UserListAPI.subscribe,
                    filter,
                    placeholder: 'Person suchen',
                    placeholderMode: 'hide',
                    // the search also matches the email, so show it - several persons can share a name
                    renderString: entity =>
                      [
                        `${entity.profilDaten?.stammdaten?.vorname ?? ''} ${entity.profilDaten?.stammdaten?.nachname ?? ''}`.trim(),
                        entity.debitorennummer && `(${entity.debitorennummer})`,
                        entity.email && `· ${entity.email}`,
                      ]
                        .filter(Boolean)
                        .join(' '),
                    selectionSet: PICKER_SELECTION,
                    onEntityPicked: entity => {
                      pick(
                        entity
                          ? {
                              _id: entity._id,
                              vorname: entity.profilDaten?.stammdaten?.vorname,
                              nachname: entity.profilDaten?.stammdaten?.nachname,
                              debitorennummer: entity.debitorennummer,
                            }
                          : null,
                      );
                    },
                  })}
                </label>
                ${
                  canCreate
                    ? html`
                        <button type="button" class="btn btn-ghost btn-sm self-start" @click=${() => setCreating(true)}>
                          <span class="icon-[material-symbols--person-add-outline-rounded] size-5"></span>
                          Neue Person anlegen
                        </button>
                      `
                    : nothing
                }
              `
        }
        ${
          complete
            ? html`<p>
                <b>${label(left)}</b> ist ${isSB ? 'sorgeberechtigt für' : 'Spender für'} <b>${label(right)}</b>.
              </p>`
            : left && right
              ? html`<p class="text-error">Eine Person kann nicht mit sich selbst verknüpft werden.</p>`
              : nothing
        }
        ${
          complete && !allowed && !unchanged
            ? html`<p class="text-sm text-base-content/70">Für diese Änderung fehlt Ihnen die Berechtigung.</p>`
            : nothing
        }
        ${
          // replacing goes through removing, which ends the mandates the old link carried
          props.link && complete && !samePair
            ? html`<div class="alert alert-warning alert-soft">
                Die bisherige Beziehung wird entfernt. Laufende SEPA-Mandate, die die bisherige Person für die Buchungen
                des Kindes trägt, werden beendet.
              </div>`
            : nothing
        }

        <div class="flex flex-wrap items-center justify-between gap-2">
          ${
            props.link && canRemove(props.link.inviteType)
              ? html`
                  <button type="button" class="btn btn-outline btn-error" @click=${handleRemove}>
                    <span class="icon-[material-symbols--link-off-rounded] size-5"></span>
                    Beziehung entfernen
                  </button>
                `
              : html`<span></span>`
          }
          <div class="flex gap-2">
            <button
              type="button"
              class="btn btn-primary"
              ?disabled=${!complete || !allowed || unchanged}
              @click=${handleSave}
            >
              ${props.link ? 'Speichern' : 'Hinzufügen'}
            </button>
          </div>
        </div>
      </div>
    `;
  },
);
